import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import type { OrgPhoto } from '@/lib/partnerTypes';

interface Props {
  orgId: string;
  canEdit: boolean;
  onCountChange?: (count: number) => void;
}

function Lightbox({ photos, startIndex, onClose }: {
  photos: OrgPhoto[];
  startIndex: number;
  onClose: () => void;
}) {
  const [current, setCurrent] = useState(startIndex);

  const prev = () => setCurrent(i => (i - 1 + photos.length) % photos.length);
  const next = () => setCurrent(i => (i + 1) % photos.length);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const photo = photos[current];

  return (
    <div
      className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Close */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-white/70 hover:text-white bg-white/10 rounded-full cursor-pointer transition-colors"
      >
        <i className="ri-close-line text-xl" />
      </button>

      {/* Counter */}
      <span className="absolute top-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">
        {current + 1} / {photos.length}
      </span>

      {/* Prev */}
      {photos.length > 1 && (
        <button
          onClick={e => { e.stopPropagation(); prev(); }}
          className="absolute left-4 md:left-8 w-10 h-10 flex items-center justify-center text-white/70 hover:text-white bg-white/10 rounded-full cursor-pointer transition-colors"
        >
          <i className="ri-arrow-left-s-line text-xl" />
        </button>
      )}

      {/* Image */}
      <div className="px-20 max-h-screen flex flex-col items-center gap-3" onClick={e => e.stopPropagation()}>
        <img
          src={photo.url}
          alt={photo.caption ?? ''}
          className="max-h-[80vh] max-w-[85vw] object-contain rounded-lg"
        />
        {photo.caption && (
          <p className="text-white/80 text-sm text-center max-w-md leading-relaxed">
            {photo.caption}
          </p>
        )}
      </div>

      {/* Next */}
      {photos.length > 1 && (
        <button
          onClick={e => { e.stopPropagation(); next(); }}
          className="absolute right-4 md:right-8 w-10 h-10 flex items-center justify-center text-white/70 hover:text-white bg-white/10 rounded-full cursor-pointer transition-colors"
        >
          <i className="ri-arrow-right-s-line text-xl" />
        </button>
      )}
    </div>
  );
}

function PhotoCard({ photo, index, canEdit, onClick, onDelete, onCaptionSave }: {
  photo: OrgPhoto;
  index: number;
  canEdit: boolean;
  onClick: () => void;
  onDelete: (id: string) => void;
  onCaptionSave: (id: string, caption: string) => void;
}) {
  const [editingCaption, setEditingCaption] = useState(false);
  const [captionVal, setCaptionVal] = useState(photo.caption ?? '');
  const [hovering, setHovering] = useState(false);

  const handleSaveCaption = () => {
    onCaptionSave(photo.id, captionVal);
    setEditingCaption(false);
  };

  return (
    <div
      className="group relative bg-stone-100 rounded-xl overflow-hidden border border-stone-200 hover:border-[#1a2e4a]/20 transition-all"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      {/* Image */}
      <div
        className="aspect-square cursor-pointer overflow-hidden relative"
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && onClick()}
        aria-label={`Open photo ${index + 1}`}
      >
        <img
          src={photo.url}
          alt={photo.caption ?? `Photo ${index + 1}`}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        {/* Hover overlay */}
        <div className={`absolute inset-0 bg-black/30 flex items-center justify-center transition-opacity duration-200 ${hovering ? 'opacity-100' : 'opacity-0'}`}>
          <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full backdrop-blur-sm">
            <i className="ri-zoom-in-line text-white text-lg" />
          </div>
        </div>
      </div>

      {/* Caption area */}
      <div className="p-2.5">
        {editingCaption ? (
          <div className="flex items-center gap-1.5">
            <input
              autoFocus
              value={captionVal}
              onChange={e => setCaptionVal(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') handleSaveCaption();
                if (e.key === 'Escape') { setCaptionVal(photo.caption ?? ''); setEditingCaption(false); }
              }}
              placeholder="Add caption..."
              className="flex-1 text-xs border border-stone-200 rounded-md px-2 py-1 focus:outline-none focus:border-[#1a2e4a] min-w-0"
            />
            <button
              onClick={handleSaveCaption}
              className="w-6 h-6 flex items-center justify-center text-emerald-600 hover:bg-emerald-50 rounded cursor-pointer"
            >
              <i className="ri-check-line text-sm" />
            </button>
            <button
              onClick={() => { setCaptionVal(photo.caption ?? ''); setEditingCaption(false); }}
              className="w-6 h-6 flex items-center justify-center text-stone-400 hover:bg-stone-100 rounded cursor-pointer"
            >
              <i className="ri-close-line text-sm" />
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-1">
            <p
              className={`text-xs truncate leading-tight ${photo.caption ? 'text-stone-500' : 'text-stone-300 italic'}`}
              title={photo.caption ?? ''}
            >
              {photo.caption || (canEdit ? 'No caption' : '')}
            </p>
            <div className="flex items-center gap-0.5 flex-shrink-0">
              {canEdit && (
                <>
                  <button
                    onClick={() => setEditingCaption(true)}
                    className="w-6 h-6 flex items-center justify-center text-stone-300 hover:text-[#1a2e4a] hover:bg-stone-100 rounded cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Edit caption"
                  >
                    <i className="ri-pencil-line text-xs" />
                  </button>
                  <button
                    onClick={() => onDelete(photo.id)}
                    className="w-6 h-6 flex items-center justify-center text-stone-300 hover:text-red-500 hover:bg-red-50 rounded cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Delete photo"
                  >
                    <i className="ri-delete-bin-line text-xs" />
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function PhotosTab({ orgId, canEdit, onCountChange }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [photos, setPhotos] = useState<OrgPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadCount, setUploadCount] = useState(0);
  const [uploadTotal, setUploadTotal] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const fetchPhotos = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('org_photos')
      .select('*')
      .eq('organization_id', orgId)
      .order('created_at', { ascending: false });
    setPhotos(data ?? []);
    setLoading(false);
    onCountChange?.((data ?? []).length);
  }, [orgId]);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const uploadFiles = async (files: FileList | File[]) => {
    const fileArr = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (fileArr.length === 0) { setError('Please select image files (JPG, PNG, WEBP, GIF).'); return; }

    const oversize = fileArr.find(f => f.size > 15 * 1024 * 1024);
    if (oversize) { setError(`"${oversize.name}" exceeds 15 MB limit.`); return; }

    setUploading(true);
    setError('');
    setUploadCount(0);
    setUploadTotal(fileArr.length);

    let successCount = 0;
    for (const file of fileArr) {
      const ext = file.name.split('.').pop() ?? 'jpg';
      const path = `${orgId}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;

      const { error: upErr } = await supabase.storage
        .from('org-photos')
        .upload(path, file, { contentType: file.type });

      if (upErr) {
        setError(`Upload failed: ${upErr.message}`);
        continue;
      }

      const { data: urlData } = supabase.storage.from('org-photos').getPublicUrl(path);

      await supabase.from('org_photos').insert({
        organization_id: orgId,
        url: urlData.publicUrl,
        caption: null,
      });

      successCount++;
      setUploadCount(c => c + 1);
    }

    setUploading(false);
    setUploadTotal(0);
    setUploadCount(0);
    if (successCount > 0) {
      showToast(`${successCount} photo${successCount > 1 ? 's' : ''} uploaded successfully.`);
      fetchPhotos();
    }
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) uploadFiles(e.target.files);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (canEdit && e.dataTransfer.files) uploadFiles(e.dataTransfer.files);
  };

  const handleDelete = async (photoId: string) => {
    const photo = photos.find(p => p.id === photoId);
    if (!photo) return;
    setDeleting(photoId);

    const urlParts = photo.url.split('/org-photos/');
    if (urlParts.length > 1) {
      const storagePath = decodeURIComponent(urlParts[1].split('?')[0]);
      await supabase.storage.from('org-photos').remove([storagePath]);
    }

    const { error: delErr } = await supabase.from('org_photos').delete().eq('id', photoId);
    if (delErr) {
      setError(delErr.message);
    } else {
      if (lightboxIndex !== null) {
        const newPhotos = photos.filter(p => p.id !== photoId);
        if (newPhotos.length === 0) setLightboxIndex(null);
        else setLightboxIndex(prev => prev !== null ? Math.min(prev, newPhotos.length - 1) : null);
      }
      setPhotos(prev => {
        const next = prev.filter(p => p.id !== photoId);
        onCountChange?.(next.length);
        return next;
      });
      showToast('Photo deleted.');
    }
    setDeleting(null);
  };

  const handleCaptionSave = async (photoId: string, caption: string) => {
    const { error: updErr } = await supabase
      .from('org_photos')
      .update({ caption: caption.trim() || null })
      .eq('id', photoId);
    if (!updErr) {
      setPhotos(prev => prev.map(p => p.id === photoId ? { ...p, caption: caption.trim() || null } : p));
    }
  };

  return (
    <div>
      {/* Toast */}
      {toast && (
        <div className="fixed top-5 right-5 z-50 bg-emerald-600 text-white text-sm font-medium px-4 py-2.5 rounded-lg flex items-center gap-2">
          <i className="ri-check-line" />{toast}
        </div>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && photos.length > 0 && (
        <Lightbox
          photos={photos}
          startIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-sm font-bold text-[#1a2e4a]">Photo Gallery ({photos.length})</h3>
          <p className="text-xs text-stone-400 mt-0.5">JPG, PNG, WEBP — max 15 MB each. Multiple upload supported.</p>
        </div>
        {canEdit && (
          <>
            <button
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-2 bg-[#1a2e4a] text-white text-xs font-semibold px-3.5 py-2 rounded-lg hover:bg-[#253f62] disabled:opacity-60 cursor-pointer whitespace-nowrap transition-colors"
            >
              {uploading ? (
                <><i className="ri-loader-4-line animate-spin" />Uploading {uploadCount}/{uploadTotal}</>
              ) : (
                <><i className="ri-image-add-line" />Upload Photos</>
              )}
            </button>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={handleFileInput}
            />
          </>
        )}
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 flex items-center gap-2 text-sm text-red-600">
          <i className="ri-error-warning-line" />
          {error}
          <button onClick={() => setError('')} className="ml-auto cursor-pointer"><i className="ri-close-line" /></button>
        </div>
      )}

      {loading ? (
        <div className="py-16 flex items-center justify-center">
          <i className="ri-loader-4-line text-2xl text-stone-300 animate-spin" />
        </div>
      ) : photos.length === 0 ? (
        /* Empty drop zone */
        <div
          className={`py-16 text-center border-2 border-dashed rounded-xl transition-colors ${dragOver ? 'border-[#1a2e4a] bg-[#1a2e4a]/4' : 'border-stone-200'}`}
          onDragOver={e => { e.preventDefault(); if (canEdit) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <div className="w-14 h-14 flex items-center justify-center bg-stone-100 rounded-2xl mx-auto mb-4">
            <i className="ri-image-2-line text-stone-400 text-2xl" />
          </div>
          <p className="text-sm font-semibold text-stone-500">No photos yet</p>
          {canEdit && (
            <>
              <p className="text-xs text-stone-400 mt-1 mb-4">Click <strong>Upload Photos</strong> or drag & drop images here</p>
              <button
                onClick={() => inputRef.current?.click()}
                className="inline-flex items-center gap-2 text-xs font-semibold text-[#1a2e4a] bg-[#1a2e4a]/8 hover:bg-[#1a2e4a]/12 px-4 py-2 rounded-lg cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-upload-cloud-2-line" />Choose Photos
              </button>
            </>
          )}
        </div>
      ) : (
        /* Gallery grid */
        <div
          className={`transition-colors rounded-xl ${dragOver ? 'ring-2 ring-[#1a2e4a]/30' : ''}`}
          onDragOver={e => { e.preventDefault(); if (canEdit) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {photos.map((photo, index) => (
              <PhotoCard
                key={photo.id}
                photo={photo}
                index={index}
                canEdit={canEdit && deleting !== photo.id}
                onClick={() => setLightboxIndex(index)}
                onDelete={handleDelete}
                onCaptionSave={handleCaptionSave}
              />
            ))}
            {/* Upload tile when gallery has items */}
            {canEdit && (
              <button
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="aspect-square border-2 border-dashed border-stone-200 rounded-xl flex flex-col items-center justify-center gap-2 text-stone-400 hover:border-[#1a2e4a]/30 hover:text-[#1a2e4a] cursor-pointer transition-colors disabled:opacity-50 group"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-stone-100 group-hover:bg-[#1a2e4a]/8 rounded-full transition-colors">
                  <i className="ri-add-line text-xl" />
                </div>
                <span className="text-xs font-medium">Add more</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
